import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CellFormat } from '../compositor/GridController';
import {
    applyFormulaKeyCommand,
    getFormulaFunctionAssistance,
    insertFormulaFunctionSuggestion,
    isActiveFormulaReferenceEditSession,
    resolveFormulaKeyCommand,
    type FormulaCommitNavigation,
    type FormulaEditSession,
    updateFormulaDraft,
    updateFormulaSelection,
} from '../spreadsheet/formulaAuthoring';
import { getFormulaNameAutocomplete, type FormulaNameAutocompleteSuggestion } from '../spreadsheet/formulaNameAutocomplete';
import { getDefaultFontStack } from '../tableau/fontRegistry';
import FormulaFunctionAssistant from './FormulaFunctionAssistant';
import FormulaSyntaxOverlay from './FormulaSyntaxOverlay';

const DEFAULT_EDITOR_FONT_FAMILY = getDefaultFontStack('ui')

interface CellEditorProps {
    initialValue: string;
    rect: { x: number, y: number, w: number, h: number };
    format?: CellFormat;
    onCommit: (value: string, navigation?: FormulaCommitNavigation | null) => void;
    onCancel: () => void;
    onChange?: (value: string, caretStart?: number, caretEnd?: number) => void;
    formulaSession?: FormulaEditSession | null;
    formulaDefinedNames?: FormulaNameAutocompleteSuggestion[];
    onFormulaSessionChange?: (session: FormulaEditSession) => void;
    showFunctionAssistant?: boolean;
    onActivateFormulaSurface?: () => void;
}

const arrowCommitNavigation: Record<string, FormulaCommitNavigation> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
};

export const CellEditor: React.FC<CellEditorProps> = ({
    initialValue,
    rect,
    format,
    onCommit,
    onCancel,
    onChange,
    formulaSession,
    formulaDefinedNames = [],
    onFormulaSessionChange,
    showFunctionAssistant = true,
    onActivateFormulaSurface,
}) => {
    const [value, setValue] = useState(initialValue);
    const [activeNameSuggestionIndex, setActiveNameSuggestionIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
        }
    }, []);

    useEffect(() => {
        if (formulaSession && formulaSession.draft !== value) {
            setValue(formulaSession.draft);
        }
        if (formulaSession) {
            requestAnimationFrame(() => {
                const node = inputRef.current;
                if (node && document.activeElement === node) {
                    node.setSelectionRange(formulaSession.caretStart, formulaSession.caretEnd);
                }
            });
        }
    }, [formulaSession, value]);

    const functionAssistance = useMemo(() => (
        formulaSession
            ? getFormulaFunctionAssistance(value, formulaSession.caretEnd, formulaSession.functionSuggestionIndex)
            : null
    ), [formulaSession, value]);
    const nameAutocomplete = useMemo(() => {
        if (!formulaSession || functionAssistance?.suggestions.length) return null;
        return getFormulaNameAutocomplete(value, formulaSession.caretEnd, formulaDefinedNames);
    }, [formulaDefinedNames, formulaSession, functionAssistance?.suggestions.length, value]);
    const nameSuggestionIndex = nameAutocomplete
        ? Math.min(activeNameSuggestionIndex, nameAutocomplete.suggestions.length - 1)
        : 0;

    useEffect(() => {
        setActiveNameSuggestionIndex(0);
    }, [nameAutocomplete?.query, nameAutocomplete?.startOffset]);

    const handleNameSuggestionSelect = (name: string) => {
        if (!formulaSession || !nameAutocomplete) return;
        const replaced = `${formulaSession.draft.slice(0, nameAutocomplete.startOffset)}${name}${formulaSession.draft.slice(formulaSession.caretEnd)}`;
        const caret = nameAutocomplete.startOffset + name.length;
        const nextSession = updateFormulaDraft(formulaSession, replaced, caret, caret);
        onFormulaSessionChange?.(nextSession);
        setValue(nextSession.draft);
        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(nextSession.caretStart, nextSession.caretEnd);
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const arrowNavigation = arrowCommitNavigation[e.key];
        if (
            arrowNavigation &&
            !e.shiftKey &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey &&
            (!formulaSession || formulaSession.mode === 'value')
        ) {
            e.preventDefault();
            onCommit(value, arrowNavigation);
            return;
        }

        if (formulaSession) {
            if (nameAutocomplete?.suggestions.length && !functionAssistance?.suggestions.length) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    e.stopPropagation();
                    const delta = e.key === 'ArrowDown' ? 1 : -1;
                    const count = nameAutocomplete.suggestions.length;
                    setActiveNameSuggestionIndex((current) => (current + delta + count) % count);
                    return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNameSuggestionSelect(nameAutocomplete.suggestions[nameSuggestionIndex]?.name ?? nameAutocomplete.suggestions[0].name);
                    return;
                }
            }
            const command = {
                key: e.key,
                shiftKey: e.shiftKey,
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                altKey: e.altKey,
            };
            const keyDecision = resolveFormulaKeyCommand(formulaSession, command);
            const nextSession = applyFormulaKeyCommand(formulaSession, command);
            if (nextSession !== formulaSession) {
                e.preventDefault();
                if (keyDecision.stopPropagation) e.stopPropagation();
                onFormulaSessionChange?.(nextSession);
                if (nextSession.status === 'committed') {
                    onCommit(nextSession.draft, nextSession.commitNavigation);
                    return;
                }
                if (nextSession.status === 'cancelled') {
                    onCancel();
                    return;
                }
                if (nextSession.draft !== value) {
                    setValue(nextSession.draft);
                }
                return;
            }
            if (keyDecision.preventDefault) e.preventDefault();
            if (keyDecision.stopPropagation) e.stopPropagation();
            if (keyDecision.disposition === 'host-suppressed') return;
        }
        if (e.key === 'Enter') {
            onCommit(value, e.shiftKey ? 'up' : 'down');
        } else if (e.key === 'Escape') {
            onCancel();
        } else if (e.key === 'Tab') {
            e.preventDefault();
            onCommit(value, e.shiftKey ? 'left' : 'right');
        }
    };

    const handleBlur = () => {
        if (isActiveFormulaReferenceEditSession(formulaSession)) return;
        onCommit(value, 'none');
    };

    const handleFormulaSuggestionSelect = (functionName: string) => {
        if (!formulaSession) return;
        const nextSession = insertFormulaFunctionSuggestion(formulaSession, functionName);
        if (nextSession === formulaSession) return;
        onFormulaSessionChange?.(nextSession);
        if (nextSession.draft !== value) {
            setValue(nextSession.draft);
        }
        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(nextSession.caretStart, nextSession.caretEnd);
        });
    };

    const handleSelectionChange = (target: HTMLInputElement) => {
        if (!formulaSession) return;
        const caretStart = target.selectionStart ?? target.value.length;
        const caretEnd = target.selectionEnd ?? caretStart;
        const nextSession = updateFormulaSelection(formulaSession, caretStart, caretEnd);
        if (
            nextSession.caretStart === formulaSession.caretStart &&
            nextSession.caretEnd === formulaSession.caretEnd &&
            nextSession.activeReferenceId === formulaSession.activeReferenceId &&
            nextSession.mode === formulaSession.mode &&
            nextSession.functionSuggestionIndex === formulaSession.functionSuggestionIndex
        ) {
            return;
        }
        onFormulaSessionChange?.(nextSession);
    };

    const textColor = format?.textColor || '#000000';
    const textAlign: React.CSSProperties['textAlign'] =
        format?.align === 'distributed' || format?.align === 'justify'
            ? 'justify'
            : format?.align === 'center' || format?.align === 'right' || format?.align === 'left'
                ? format.align
                : 'left';
    const editorTextStyles: React.CSSProperties = {
        fontSize: `${format?.fontSize || 13}px`,
        fontFamily: format?.fontFamily || DEFAULT_EDITOR_FONT_FAMILY,
        fontWeight: format?.bold ? 'bold' : 'normal',
        fontStyle: format?.italic ? 'italic' : 'normal',
        textDecoration: format?.underline ? 'underline' : 'none',
        textAlign,
        lineHeight: `${Math.max(12, rect.h - 4)}px`,
    };

    const containerStyles: React.CSSProperties = {
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        zIndex: 100,
        pointerEvents: 'auto',
    };

    const inputStyles: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: '2px solid var(--fg-selection-border-strong)',
        outline: 'none',
        padding: '0 5px', // Match TextRenderer padding if possible, or close enough
        boxSizing: 'border-box',
        backgroundColor: formulaSession && formulaSession.mode !== 'value' ? 'transparent' : (format?.backgroundColor || 'white'),
        color: formulaSession && formulaSession.mode !== 'value' ? 'transparent' : textColor,
        caretColor: textColor,
        ...editorTextStyles,
    };

    const overlayStyles: React.CSSProperties = {
        border: '2px solid transparent',
        padding: '0 5px',
        boxSizing: 'border-box',
        backgroundColor: format?.backgroundColor || 'white',
        ...editorTextStyles,
    };

    return (
        <div style={containerStyles}>
            <FormulaSyntaxOverlay
                session={formulaSession}
                text={value}
                style={overlayStyles}
            />
            <FormulaFunctionAssistant
                session={showFunctionAssistant ? formulaSession : null}
                text={value}
                nameSuggestions={nameAutocomplete?.suggestions ?? []}
                activeNameSuggestionIndex={nameSuggestionIndex}
                onSelectSuggestion={handleFormulaSuggestionSelect}
                onSelectNameSuggestion={handleNameSuggestionSelect}
            />
            <input
                ref={inputRef}
                id="cell-editor-input"
                name="cell-editor-input"
                type="text"
                value={value}
                onChange={(e) => {
                    const nextValue = e.target.value;
                    setValue(nextValue);
                    if (onChange) {
                        onChange(
                            nextValue,
                            e.target.selectionStart ?? nextValue.length,
                            e.target.selectionEnd ?? e.target.selectionStart ?? nextValue.length,
                        );
                    }
                }}
                onKeyDown={handleKeyDown}
                onFocus={onActivateFormulaSurface}
                onSelect={(e) => handleSelectionChange(e.currentTarget)}
                onClick={(e) => handleSelectionChange(e.currentTarget)}
                onKeyUp={(e) => handleSelectionChange(e.currentTarget)}
                onBlur={handleBlur}
                style={inputStyles}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-form-type="other"
            />
        </div>
    );
};
